import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Mail, Phone, Linkedin } from "lucide-react";
import { useSiteData } from "../context/SiteData";
import { findImage, resolveImage } from "../lib/api";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

// Reveal-on-scroll wrapper
export function Reveal({ children, className = "", delay = 0, as: Tag = "div" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

// Animated counter
export function CountUp({ value, isYear = false, duration = 1600 }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const from = isYear ? 1970 : 0;
        const step = (now) => {
          const p = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setDisplay(Math.round(from + (value - from) * eased));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [value, isYear, duration]);
  return <span ref={ref}>{display}</span>;
}

export function Eyebrow({ children, muted = false, className = "" }) {
  return <span className={`eyebrow ${muted ? "muted" : ""} ${className}`}>{children}</span>;
}

// Inner-page header with breadcrumb
export function PageHeader({ crumb, title, intro }) {
  return (
    <section className="hero-wash border-b border-[var(--rs-border)]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-14 md:pt-32 md:pb-20">
        <Reveal>
          <nav className="flex items-center gap-1.5 text-sm text-[var(--rs-muted)] font-mono-label">
            <Link to="/" className="hover:text-[var(--rs-blue)] transition-colors">Home</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--rs-ink)]">{crumb}</span>
          </nav>
        </Reveal>
        <Reveal delay={80}>
          <h1 className="font-head font-extrabold text-[var(--rs-ink)] text-4xl md:text-6xl leading-[1.05] mt-6 max-w-4xl">
            {title}
          </h1>
        </Reveal>
        {intro && (
          <Reveal delay={160}>
            <p className="text-[var(--rs-muted)] text-lg leading-relaxed mt-6 max-w-2xl">{intro}</p>
          </Reveal>
        )}
      </div>
    </section>
  );
}

export function SectionLabel({ children }) {
  return <div className="mb-4"><Eyebrow>{children}</Eyebrow></div>;
}

// Director photo, admin-editable via admin.html's Images tab (catalog id
// "director-<slugified-name>"). Falls back to the initials circle already
// used across About/History/Career until a real photo is uploaded.
export function DirectorAvatar({ director, size = 80 }) {
  const { data, loaded } = useSiteData();
  const imgId = "director-" + director.name.toLowerCase().replace(/\s+/g, "-");
  const entry = loaded ? findImage(data.images, imgId) : null;
  const src = entry ? resolveImage(entry.path, null) : null;

  if (src) {
    return (
      <img
        src={src}
        alt={director.name}
        style={{ width: size, height: size }}
        className="rounded-full object-cover shrink-0"
      />
    );
  }
  return (
    <div
      style={{ width: size, height: size, fontSize: Math.round(size * 0.3) }}
      className="rounded-full bg-gradient-to-br from-[var(--rs-blue)] to-[#4a7ef0] text-white font-head font-bold flex items-center justify-center shrink-0"
    >
      {director.initials}
    </div>
  );
}

// Wraps a director card (passed as children, unchanged) so clicking it opens
// a profile dialog with contact details — only renders the fields a given
// director actually has, so a director without a LinkedIn/phone on file yet
// doesn't show blank rows.
export function DirectorProfileDialog({ director, children }) {
  const hasAnyContact = director.email || director.phone || director.linkedin;
  return (
    <Dialog>
      <DialogTrigger asChild>
        <div className="cursor-pointer">{children}</div>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <DirectorAvatar director={director} size={64} />
            <div>
              <DialogTitle className="font-head text-xl">{director.name}</DialogTitle>
              <DialogDescription className="text-[var(--rs-blue)] font-medium">{director.role}</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="mt-2 space-y-3">
          {director.email && (
            <a href={`mailto:${director.email}`} className="flex items-center gap-3 text-sm text-[var(--rs-ink)] hover:text-[var(--rs-blue)] transition-colors">
              <Mail size={16} className="text-[var(--rs-blue)] shrink-0" /> {director.email}
            </a>
          )}
          {director.phone && (
            <a href={`tel:${director.phone.replace(/[^\d+]/g, "")}`} className="flex items-center gap-3 text-sm text-[var(--rs-ink)] hover:text-[var(--rs-blue)] transition-colors">
              <Phone size={16} className="text-[var(--rs-blue)] shrink-0" /> {director.phone}
            </a>
          )}
          {director.linkedin && (
            <a href={director.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-[var(--rs-ink)] hover:text-[var(--rs-blue)] transition-colors">
              <Linkedin size={16} className="text-[var(--rs-blue)] shrink-0" /> LinkedIn Profile
            </a>
          )}
          {!hasAnyContact && (
            <p className="text-sm text-[var(--rs-muted)]">Contact details coming soon.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
